const TOP_SERVICE_CHART_LIMIT = 10;

const toNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const getServiceName = (service = {}) => service.serviceName || service.name || "";

const getPointValue = (point = {}) =>
  toNumber(point.rate ?? point.total ?? point.value ?? point.count);

const getPointLabel = (point = {}) => point.date ?? point.day ?? point.name ?? "";

const standardDeviation = (values = [], mean = 0) => {
  if (!values.length) return 0;
  const variance = values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance);
};

export const resolveVolatilityBand = (score) => {
  if (score >= 1) return "High";
  if (score >= 0.5) return "Medium";
  return "Low";
};

const getPeakPoint = (chart = []) =>
  chart.reduce(
    (peak, point) => {
      const value = getPointValue(point);
      if (value > peak.peakValue) {
        return { peakDay: getPointLabel(point), peakValue: value };
      }
      return peak;
    },
    { peakDay: null, peakValue: 0 }
  );

const sortByDemand = (left, right) => {
  const totalDiff = toNumber(right.total) - toNumber(left.total);
  if (totalDiff !== 0) return totalDiff;
  return getServiceName(left).localeCompare(getServiceName(right), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

export const buildArrivalRatesPerServiceInsights = (branch = {}) => {
  const services = Array.isArray(branch.servicesData) ? branch.servicesData : [];
  const branchTotalArrivals =
    toNumber(branch.branchTotalArrivals) ||
    toNumber(branch.organizationTotalArrivals) ||
    services.reduce((sum, service) => sum + toNumber(service.total), 0);

  let cumulativeTotal = 0;
  const rankedServices = [...services].sort(sortByDemand).map((service, index) => {
    const chart = Array.isArray(service.chart) ? service.chart : [];
    const values = chart.map(getPointValue);
    const businessDays = values.length;
    const activeDays = service.activeDays ?? values.filter((value) => value > 0).length;
    const total = toNumber(service.total);
    const averagePerBusinessDay =
      service.averagePerBusinessDay ?? (businessDays ? total / businessDays : 0);
    const averageOnActiveDays =
      service.averageOnActiveDays ?? (activeDays ? total / activeDays : 0);
    const volatilityScore =
      service.volatilityScore ??
      (averagePerBusinessDay > 0 ? standardDeviation(values, averagePerBusinessDay) / averagePerBusinessDay : 0);
    const totalPct = service.totalPct ?? (branchTotalArrivals ? (total / branchTotalArrivals) * 100 : 0);
    cumulativeTotal += total;
    const peak = service.peakDay !== undefined
      ? { peakDay: service.peakDay, peakValue: toNumber(service.peakValue) }
      : getPeakPoint(chart);

    return {
      ...service,
      serviceName: getServiceName(service),
      total,
      totalPct,
      rank: service.rank || index + 1,
      averagePerBusinessDay,
      averageOnActiveDays,
      activeDays,
      zeroArrivalBusinessDays: service.zeroArrivalBusinessDays ?? Math.max(businessDays - activeDays, 0),
      peakDay: peak.peakDay,
      peakValue: peak.peakValue,
      volatilityScore,
      volatilityBand: service.volatilityBand || resolveVolatilityBand(volatilityScore),
      cumulativePct: service.cumulativePct ?? (branchTotalArrivals ? (cumulativeTotal / branchTotalArrivals) * 100 : 0),
    };
  });

  const nonZeroServices = rankedServices.filter((service) => service.total > 0);
  const topService = nonZeroServices[0] || rankedServices[0] || null;
  const peakService = [...rankedServices].sort((left, right) => toNumber(right.peakValue) - toNumber(left.peakValue))[0] || null;
  const volatileService =
    [...rankedServices].sort((left, right) => toNumber(right.volatilityScore) - toNumber(left.volatilityScore))[0] || null;

  return {
    branchName: branch.branchName || branch.scopeName || "",
    branchTotalArrivals,
    serviceCount: services.length,
    highVolatilityCount: rankedServices.filter((service) => service.volatilityBand === "High").length,
    rankedServices,
    topChartServices: nonZeroServices.slice(0, TOP_SERVICE_CHART_LIMIT),
    topService,
    peakService,
    volatileService,
  };
};

export { TOP_SERVICE_CHART_LIMIT };
